import React from "react";
import { useTranslation } from "react-i18next";
import {
  useSystemNotificationSettings,
  useUpdateSystemNotificationSettings,
} from "@/hooks/useNotifications";
import { Switch } from "@/components/ui/switch";
import type {
  KnownNotificationEvent,
  NotificationType,
  SystemNotificationPolicyCell,
  SystemNotificationPolicyMatrixItem,
  SystemNotificationRecipientGroup,
} from "@/types/notifications";

const CATEGORY_ORDER: NotificationType[] = [
  "BOOKING",
  "TASK",
  "MESSAGE",
  "SYSTEM",
  "SERVICE",
];

function getCell(
  row: SystemNotificationPolicyMatrixItem,
  group: SystemNotificationRecipientGroup,
): SystemNotificationPolicyCell | undefined {
  return row.settings.find((setting) => setting.recipientGroup === group);
}

export const NotificationsSettingsPage = () => {
  const { t } = useTranslation();
  const settingsQuery = useSystemNotificationSettings(true);
  const updateMutation = useUpdateSystemNotificationSettings();

  const matrix = settingsQuery.data;
  const isBusy = settingsQuery.isLoading || updateMutation.isPending;

  const rows = React.useMemo(() => {
    return [...(matrix?.events ?? [])].sort((left, right) => {
      const categoryDelta =
        CATEGORY_ORDER.indexOf(left.category) -
        CATEGORY_ORDER.indexOf(right.category);

      if (categoryDelta !== 0) return categoryDelta;
      return left.event.localeCompare(right.event);
    });
  }, [matrix?.events]);

  const updateCell = React.useCallback(
    (
      event: KnownNotificationEvent,
      recipientGroup: SystemNotificationRecipientGroup,
      enabled: boolean,
    ) => {
      updateMutation.mutate({
        policies: [{ event, recipientGroup, enabled }],
      });
    },
    [updateMutation],
  );

  if (settingsQuery.isLoading) {
    return <div className="flex flex-col space-y-6 p-12" />;
  }

  if (settingsQuery.isError) {
    return (
      <div className="flex flex-col space-y-6 p-12">
        <div className="text-lg text-red-600">
          {t("notifications.failedToLoad")}
        </div>
      </div>
    );
  }

  if (!matrix) {
    return <div className="flex flex-col space-y-6 p-12" />;
  }

  return (
    <div className="flex flex-col gap-6 p-12">
      <div className="flex flex-col gap-1">
        <h1 className="text-[24px] font-semibold text-[#111827]">
          {t("notifications.systemSettings.title")}
        </h1>
      </div>

      <div className="overflow-hidden border border-[#E5E7EB] bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] table-fixed border-collapse text-[14px]">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB] text-left text-[12px] font-semibold uppercase text-[#6B7280]">
                <th className="w-[160px] px-4 py-3">
                  {t("notifications.systemSettings.category")}
                </th>
                <th className="px-4 py-3">
                  {t("notifications.systemSettings.type")}
                </th>
                {matrix.recipientGroups.map((group) => (
                  <th key={group} className="w-[132px] px-4 py-3 text-center">
                    {t(`notifications.systemSettings.groups.${group}`)}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.event}
                  className="border-b border-[#E5E7EB] last:border-b-0"
                >
                  <td className="px-4 py-4 align-middle text-[13px] font-medium text-[#6B7280]">
                    {t(
                      `notifications.systemSettings.categories.${row.category}`,
                    )}
                  </td>
                  <td className="px-4 py-4 align-middle">
                    <div className="text-[14px] font-medium text-[#111827]">
                      {t(`notifications.systemSettings.events.${row.event}`)}
                    </div>
                  </td>

                  {matrix.recipientGroups.map((group) => {
                    const cell = getCell(row, group);
                    const applicable = Boolean(cell?.applicable);
                    const checked = Boolean(cell?.enabled);
                    const ariaLabel = t(
                      "notifications.systemSettings.toggleAria",
                      {
                        event: t(
                          `notifications.systemSettings.events.${row.event}`,
                        ),
                        group: t(
                          `notifications.systemSettings.groups.${group}`,
                        ),
                      },
                    );

                    return (
                      <td
                        key={`${row.event}:${group}`}
                        className="px-4 py-4 text-center align-middle"
                      >
                        {applicable ? (
                          <Switch
                            checked={checked}
                            disabled={isBusy}
                            aria-label={ariaLabel}
                            onCheckedChange={(next) =>
                              updateCell(row.event, group, next)
                            }
                          />
                        ) : (
                          <span className="text-[#9CA3AF]">-</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
